/*
 * TMP PHOTO SLIDESHOW
 * Original repo: https://github.com/themarshallproject/tmp-photo-slideshow
 */

// packages
import defaults from 'underscore/modules/defaults.js';
import Hammer from 'hammerjs';

// local
import { createEvent } from '$app/util/events.js';

export default class TMPSlideshow {
  constructor(config) {
    // Set default values for undefined options
    const configWithDefaults = defaults(config, {
      sizeByWindowHeight: true, // If true, photos will take up full screen height if they fit
      mobileMaxWidth: 579, // Width at which to trigger mobile full-bleed style, -1 indicates no max width
      startingSlide: 0, // Index of slide to initiate slideshow on
      captionType: 'each', // TK
    });

    /** @type {HTMLElement} */
    this.el = config.el;
    this.config = configWithDefaults;
    this.init();
  }

  init() {
    this.containerElement = this.el;

    if (this.config.slideData) {
      this.buildSlideMarkup();
    }

    this.slideshowContent =
      this.containerElement.querySelector('.slideshow-content');
    this.slideWrapper = this.containerElement.querySelector('.slide-wrapper');
    this.wrapperOffset = 0;
    this.slides = this.containerElement.querySelectorAll('.slide');
    this.captionWrapper = this.containerElement.querySelector(
      '.slide-caption-wrapper'
    );
    this.captions = this.containerElement.querySelectorAll('.slide-caption');
    this.navBtns = this.containerElement.querySelectorAll('.slideshow-btn');
    this.slideProgress =
      this.containerElement.querySelectorAll('.slide-progress');

    this.sliderManager = new Hammer.Manager(this.slideshowContent);

    this.buildProgressIcon();
    this.sizeSlideshow();
    this.goToSlide(this.config.startingSlide);
    this.initEvents();
  }

  buildSlideMarkup() {
    const slideMarkup = [];
    const captionMarkup = [];

    this.config.slideData.forEach((slide, i) => {
      let thisSrcset = '';
      let thisByline = '';

      if (slide.srcset) {
        thisSrcset = `srcset="${slide.srcset}"`;
      }

      if (slide.byline) {
        thisByline = `<span class="byline">${slide.byline}</span>`;
      }

      slideMarkup.push(`
        <div class="slide slide-${i}">
          <img src="${slide.filepath}" ${thisSrcset} alt="${slide.caption}">
        </div>
      `);

      captionMarkup.push(`
        <p class="slide-caption slide-caption-${k}">
          <span class="slide-progress"></span>
          <span class="caption-text">${slide.caption}</span>
          ${thisByline}
        </p>
      `);
    });

    const fullHTML = `
    <div class="slideshow-content">
      <div class="slide-wrapper">
        ${slideMarkup.join('')}
      </div>
    </div>
    <div class="slide-caption-wrapper">
      <a href="#" data-dir="prev" class="slideshow-btn slide-caption-btn btn-prev"></a>
        ${captionMarkup.join('')}
      <a href="#" data-dir="next" class="slideshow-btn slide-caption-btn btn-next"></a>
    </div>`;

    this.containerElement.innerHTML = fullHTML;
  }

  buildProgressIcon() {
    const iconHTML = [];

    for (var i = 0; i < this.slides.length; i++) {
      iconHTML.push('<span class="progress-icon"></span>');
    }

    this.slideProgress.forEach((element) => {
      element.innerHTML = iconHTML.join('');
    });
  }

  sizeSlideshow() {
    // Save window size values
    this.windowWidth = window.innerWidth;
    this.windowHeight = window.innerHeight;

    // Get overall width of slides
    const allSlides = this.slides;
    const allSlideImgs = Array.from(
      this.containerElement.querySelectorAll('img')
    );
    const captionElement = this.containerElement.querySelector(
      '.slide-caption-wrapper'
    );
    const captionBounds = captionElement.getBoundingClientRect();
    const captionComputedStyle = window.getComputedStyle(captionElement);
    const captionHeight =
      captionBounds.height +
      parseFloat(captionComputedStyle.marginTop) +
      parseFloat(captionComputedStyle.marginBottom);

    const maxWidth = Math.max(...allSlideImgs.map((img) => img.clientWidth));
    const maxHeight = Math.max(...allSlideImgs.map((img) => img.clientHeight));

    const sizingInfo = this.getSizingInfo(maxWidth, maxHeight, captionHeight);

    // If we want to size the slideshow to fit full height
    if (sizingInfo.measureBy === 'height') {
      allSlides.forEach((slide) => {
        slide.style.height = `${sizingInfo.slideHeight}px`;
      });
      this.slideshowContent.style.height = `${sizingInfo.slideHeight}px`;
      this.slideWrapper.style.left = `${sizingInfo.slideLeft}px`;

      let totalWidth = 0;

      allSlides.forEach((slide) => {
        slide.style.width = `${sizingInfo.slideWidth}px`;
        slide.style.left = `${totalWidth}px`;

        const slideBounds = slide.getBoundingClientRect();
        const slideComputedStyle = window.getComputedStyle(slide);

        totalWidth +=
          slideBounds.width +
          parseFloat(slideComputedStyle.marginLeft) +
          parseFloat(slideComputedStyle.marginRight);
      });
    } else {
      // Otherwise, use given CSS values for slide size
      allSlides.forEach((slide) => {
        slide.style.height = null;
      });

      this.slideshowContent.style.height = `${sizingInfo.slideHeight}px`;
      this.slideWrapper.style.left = `${sizingInfo.slideLeft}px`;

      let totalWidth = 0;

      allSlides.forEach((slide) => {
        slide.style.width = null;
        slide.style.left = `${totalWidth}px`;

        const slideBounds = slide.getBoundingClientRect();
        const slideComputedStyle = window.getComputedStyle(slide);

        totalWidth +=
          slideBounds.width +
          parseFloat(slideComputedStyle.marginLeft) +
          parseFloat(slideComputedStyle.marginRight);
      });
    }

    // Set caption width and nav button width so everything aligns
    this.containerElement.querySelector(
      '.slide-caption-wrapper'
    ).style.width = `${sizingInfo.slideWidth}px`;

    if (sizingInfo.slideLeft > 0) {
      this.containerElement.querySelector(
        '.slide-caption-btn'
      ).style.width = `${sizingInfo.slideLeft}px`;
    }
  }

  getSizingInfo(slideWidth, slideHeight, captionHeight) {
    const windowHeight = this.windowHeight;
    const windowWidth = this.windowWidth;
    const slideshowPadding = 150;
    const windowRatio =
      (windowWidth - slideshowPadding) /
      (windowHeight - slideshowPadding - captionHeight);
    const slideRatio = slideWidth / slideHeight;

    if (
      !this.config.sizeByWindowHeight ||
      windowRatio <= slideRatio ||
      windowWidth <= this.config.mobileMaxWidth
    ) {
      return {
        measureBy: 'width',
        slideHeight: slideHeight,
        slideWidth: slideWidth,
        slideLeft: (windowWidth - slideWidth) / 2,
      };
    } else {
      const computedHeight = windowHeight - slideshowPadding - captionHeight;
      const computedWidth = slideRatio * computedHeight;

      return {
        measureBy: 'height',
        slideHeight: computedHeight,
        slideWidth: computedWidth,
        slideLeft: (windowWidth - computedWidth) / 2,
      };
    }
  }

  initEvents() {
    window.addEventListener('tmp_resize', () => {
      this.resize();
    });

    const sizeSlideshow = this.sizeSlideshow.bind(this);

    this.containerElement.querySelectorAll('img').forEach((img) => {
      img.addEventListener(
        'load',
        () => {
          sizeSlideshow();
        },
        { once: true }
      );
    });

    this.navBtns.forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();

        const thisBtn = e.currentTarget;
        const activeIndex = this.activeSlideIndex;
        let newIndex;

        if (thisBtn.getAttribute('data-dir') === 'prev') {
          newIndex = activeIndex - 1;
        } else if (thisBtn.classList.contains('btn-replay')) {
          newIndex = 0;
        } else {
          newIndex = activeIndex + 1;
        }

        // Protect against exceeding range
        if (newIndex < 0 || newIndex > this.slides.length - 1) {
          return;
        }

        this.goToSlide(newIndex);
      });
    });

    // Track hover position to set cursor
    this.slideshowContent.addEventListener('mousemove', (e) => {
      const mouseX = e.clientX;

      if (mouseX < this.windowWidth / 2 && this.activeSlideIndex > 0) {
        this.slideshowContent.classList.add('slideshow-cursor-prev');
        this.slideshowContent.classList.remove(
          'slideshow-cursor-next',
          'slideshow-cursor-hide',
          'slideshow-cursor-replay'
        );
      } else if (this.activeSlideIndex == this.slides.length - 1) {
        this.slideshowContent.classList.add('slideshow-cursor-replay');
        this.slideshowContent.classList.remove(
          'slideshow-cursor-next',
          'slideshow-cursor-hide',
          'slideshow-cursor-prev'
        );
      } else {
        this.slideshowContent.classList.add('slideshow-cursor-next');
        this.slideshowContent.classList.remove(
          'slideshow-cursor-prev',
          'slideshow-cursor-hide',
          'slideshow-cursor-replay'
        );
      }
    });

    // Use Hammer.js to handle events, tap
    this.sliderManager.add(new Hammer.Tap());

    this.sliderManager.on('tap', (e) => {
      const activeIndex = this.activeSlideIndex;
      let newIndex;

      if (e.center.x < this.windowWidth / 2 && activeIndex > 0) {
        newIndex = activeIndex - 1;
      } else if (activeIndex == this.slides.length - 1) {
        newIndex = 0;
      } else {
        newIndex = activeIndex + 1;
      }

      // Protect against exceeding range
      if (newIndex < 0 || newIndex > this.slides.length - 1) {
        return;
      }

      this.goToSlide(newIndex);

      const simulatedEvent = createEvent('mousemove');
      simulatedEvent.clientX = e.center.x;
      simulatedEvent.clientY = e.center.y;
      this.slideshowContent.dispatchEvent(simulatedEvent);
    });

    // Add pan events to allow swipe/drag
    this.sliderManager.add(new Hammer.Pan({ threshold: 0, pointers: 0 }));

    // Drag along with pan movement
    this.sliderManager.on('panleft panright', (e) => {
      const currentOffset = this.wrapperOffset;
      this.slideWrapper.style.transform = `translateX(${
        currentOffset + e.deltaX
      }px)`;
    });

    // Snap to correct slide after pan ends
    this.sliderManager.on('panend', (e) => {
      let activeSlideIndex = this.activeSlideIndex;
      const sliderSensitivity =
        0.2 * this.containerElement.getBoundingClientRect().width;

      // Checking the velocity helps us handle super-quick motions like flicking
      if (e.velocityX > 1) {
        this.goToSlide(activeSlideIndex - 1);
      } else if (e.velocityX < -1) {
        this.goToSlide(activeSlideIndex + 1);
      } else {
        // We use our own check for sensitivity here instead of Hammer's threshold
        // because we still want the slide to track the mouse's movement, but we want
        // to provide a minimum distance it has to travel before advancing slides.
        if (e.deltaX <= -sliderSensitivity) {
          this.goToSlide(activeSlideIndex + 1);
        } else if (e.deltaX > sliderSensitivity) {
          this.goToSlide(activeSlideIndex - 1);
        } else {
          this.goToSlide(activeSlideIndex);
        }
      }
    });
  }

  goToSlide(inputSlideIndex) {
    let slideIndex = inputSlideIndex;
    // Prevent from moving to nonexistent slides
    if (inputSlideIndex < 0) {
      slideIndex = 0;
    } else if (inputSlideIndex > this.slides.length - 1) {
      slideIndex = this.slides.length - 1;
    }

    // go to slide
    const activeSlide = this.setActiveSlide(slideIndex);
    this.activeSlideIndex = slideIndex;

    // Move slide wrapper to display active slide
    const slideOffset = activeSlide.style.left;
    this.slideWrapper.classList.add('is-animating');
    this.slideWrapper.style.transform = `translateX(-${slideOffset})`;
    this.wrapperOffset = -1 * +slideOffset.replace('px', '');

    clearTimeout(this.animationTimer);

    this.animationTimer = setTimeout(() => {
      this.slideWrapper.classList.remove('is-animating');
    }, 700);
  }

  setActiveSlide(slideIndex) {
    // set active slide
    this.slideWrapper
      .querySelectorAll('.slide-active, .slide-prev, .slide-next')
      .forEach((slide) => {
        slide.classList.remove('slide-active', 'slide-prev', 'slide-next');
      });

    const activeSlide = this.slides[slideIndex];
    activeSlide.classList.add('slide-active');

    const progressIconActiveElements = this.captionWrapper.querySelectorAll(
      '.progress-icon-active'
    );

    progressIconActiveElements.forEach((element) => {
      element.classList.remove('progress-icon-active');
    });

    this.slideProgress.forEach((element) => {
      const progressIconElements = element.querySelectorAll('.progress-icon');
      const progressIconElement = progressIconElements[slideIndex];
      progressIconElement.classList.add('progress-icon-active');
    });

    if (slideIndex > 0) {
      this.slides[slideIndex - 1].classList.add('slide-prev');
    }

    if (slideIndex < this.slides.length - 1) {
      this.slides[slideIndex + 1].classList.add('slide-next');
    }

    if (this.config.captionType === 'each') {
      // Set active caption
      this.captionWrapper
        .querySelectorAll('.slide-caption-active')
        .forEach((caption) => {
          caption.classList.remove('slide-caption-active');
        });

      const activeCaption = this.captions[slideIndex];
      activeCaption.classList.add('slide-caption-active');
    }

    // Set button states
    const btnHide = this.containerElement.querySelector('.btn-hide');

    if (btnHide) {
      btnHide.classList.remove('btn-hide');
    }

    const btnReplay = this.containerElement.querySelector('.btn-replay');

    if (btnReplay) {
      btnReplay.classList.remove('btn-replay');
    }
    // Hide nav button if it's at start of the slideshow
    if (slideIndex === 0) {
      this.containerElement
        .querySelector('.btn-prev')
        .classList.add('btn-hide');
    } else if (slideIndex == this.slides.length - 1) {
      // Change next button to replay icon
      this.containerElement
        .querySelector('.btn-next')
        .classList.add('btn-replay');
    }

    return activeSlide;
  }

  resize() {
    this.sizeSlideshow();
    // After resizing, reset slidewrapper viewport in case image position has shifted
    this.goToSlide(this.activeSlideIndex);
  }
}
